import { useAccountType } from '@/hooks/useAccountType'
import { useIsOrgAdmin } from '@/hooks/useIsOrgAdmin'
import { useOrganizationSubscription } from '@/hooks/useOrganizationSubscription'
import { useOrganizationStore } from '@/store/organization.store'

import type { WorkspaceTrialStatus } from '@scrambl/shared'

/**
 * The current workspace's no-card trial, from the subscription payload every
 * org page already loads (no extra request). `trial` is null for personal
 * spaces, paid/activated workspaces and while the rollout flag is off.
 */
export function useWorkspaceTrial(): {
  trial: WorkspaceTrialStatus | null
  isAdmin: boolean
  organizationId: string | null
  organizationName: string
  organizationSlug: string | null
  /** Seats on the trial row, the activation dialog's starting point. */
  seats: number
  refetch: () => Promise<void>
} {
  const currentOrganization = useOrganizationStore(
    state => state.currentOrganization,
  )
  const { isPersonal } = useAccountType()
  const isAdmin = useIsOrgAdmin()
  const { subscription, refetch } = useOrganizationSubscription(
    currentOrganization?.id,
    { enabled: Boolean(currentOrganization?.id) && !isPersonal },
  )

  return {
    trial: isPersonal ? null : (subscription?.trial ?? null),
    isAdmin,
    organizationId: currentOrganization?.id ?? null,
    organizationName: currentOrganization?.name ?? 'This workspace',
    organizationSlug: currentOrganization?.slug ?? null,
    seats: subscription?.seats ?? 1,
    refetch,
  }
}
